import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Eraser } from 'lucide-react';

/**
 * Simple signature pad (mouse + touch).
 * props:
 *   value: data URL or empty
 *   onChange: (dataUrl) => void
 *   width, height, readOnly
 *
 * Iter 68 fix: previously the useEffect redrew the canvas from `value` on every
 * render. Because `setHasInk(true)` in `move()` triggered a re-render, the
 * effect would wipe the canvas mid-stroke and the stroke would visibly
 * disappear — making the pad feel broken. We now initialise the canvas once
 * via `inited` ref and only re-paint when `value` changes from EXTERNAL
 * sources (e.g. loading an existing signature on mount).
 */
const SignaturePad = forwardRef(({ value, onChange, width = 440, height = 140, readOnly = false, testid }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const inited = useRef(false);
  const lastValueRef = useRef(value);
  const [hasInk, setHasInk] = useState(!!value);

  // Init the canvas once on mount; paint any existing signature.
  useEffect(() => {
    if (inited.current) return;
    inited.current = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1A1F1A';
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
      setHasInk(true);
    }
  }, []);

  // External value change (e.g. clear from parent, or loading saved sig).
  // We DON'T wipe while the user is actively drawing.
  useEffect(() => {
    if (!inited.current) return;
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    if (drawing.current) return;  // don't wipe mid-stroke
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1A1F1A';
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
      setHasInk(true);
    } else {
      setHasInk(false);
    }
  }, [value]);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x: x * (canvas.width / rect.width), y: y * (canvas.height / rect.height) };
  };

  const start = (e) => {
    if (readOnly) return;
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
    if (!hasInk) setHasInk(true);
  };
  const move = (e) => {
    if (readOnly || !drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    lastValueRef.current = dataUrl;  // prevent the [value] effect from wiping it
    if (onChange) onChange(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    lastValueRef.current = '';
    if (onChange) onChange('');
  };

  useImperativeHandle(ref, () => ({ clear, hasInk: () => hasInk }));

  return (
    <div className="inline-block w-full" data-testid={testid}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="border border-soft rounded-lg bg-white touch-none w-full"
        style={{ maxWidth: `${width}px`, height: `${height}px` }}
        data-testid="signature-canvas"
      />
      {!readOnly && (
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[11px] text-fg-muted">Sign above with mouse or finger</span>
          <button type="button" onClick={clear} className="text-xs text-[#B83A3A] inline-flex items-center gap-1 print:hidden" data-testid="signature-clear">
            <Eraser className="w-3.5 h-3.5" /> Clear
          </button>
        </div>
      )}
    </div>
  );
});

export default SignaturePad;
